import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiCheckCircle } from "react-icons/fi";
import { cn } from "@/lib/utils";

export const OnboardingChecklist = ({
  tagLabel,
  title,
  description,
  items,
  images,
  ctaLabel,
  onCtaClick,
  className,
}) => {
  const [currentImage, setCurrentImage] = useState(0);

  // Auto-swap images every 2 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 2000);
    return () => clearInterval(timer);
  }, [images.length]);

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut", staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: "easeOut" } },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      className={cn("flex flex-col lg:flex-row gap-16 lg:gap-24 items-center w-full", className)}
    >
      {/* Left Side: Title and Checklist */}
      <div className="flex-1 w-full flex flex-col justify-center">
        {tagLabel && (
          <p className="text-brand-darkSage tracking-[0.2em] font-semibold text-xs mb-4 uppercase">
            {tagLabel}
          </p>
        )}
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brand-darkSage mb-6 leading-tight">
          {title}
        </h2>
        <p className="text-brand-lightText text-base md:text-lg mb-10 leading-relaxed max-w-lg">
          {description}
        </p>

        <ul className="space-y-6 mb-12">
          {items.map((item, index) => (
            <motion.li key={index} variants={itemVariants} className="flex gap-4 items-start group">
              <FiCheckCircle className="w-6 h-6 text-brand-gold shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110" />
              <div className="flex flex-col">
                <span className="text-brand-darkSage font-serif text-xl tracking-wide">{item.text}</span>
                {item.helperText && (
                  <span className="text-brand-lightText text-sm mt-1 leading-relaxed">
                    {item.helperText}
                  </span>
                )}
              </div>
            </motion.li>
          ))}
        </ul>

        <button
          onClick={onCtaClick}
          className="w-fit bg-brand-darkSage hover:bg-brand-gold text-white rounded-full px-10 py-4 text-xs tracking-widest uppercase font-bold transition-all shadow-lg border-none hover:shadow-xl hover:-translate-y-1 duration-300"
        >
          {ctaLabel}
        </button>
      </div>

      {/* Right Side: Auto-swapping Images */}
      <div className="flex-1 w-full max-w-md lg:max-w-none relative aspect-[4/5] rounded-t-full rounded-b-[2.5rem] overflow-hidden bg-white/50 border-8 border-white/60 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.1)]">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentImage}
            src={images[currentImage]}
            alt="Showcase"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover object-center bg-white"
          />
        </AnimatePresence>
        {/* Soft overlay gradient for framing */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none mix-blend-overlay" />
      </div>
    </motion.div>
  );
};